import StateObserver, { now, user } from 'src/StateObserver';
import { formatDuration } from 'src/replicant/utils/time';
import i18n from 'src/lib/i18n/i18n';
import * as Match2 from 'src/replicant/match2';
import { BlockGenAlgorithm, GoalsDef, MapDef } from './game/defs/map';
import { multiSizeGoals } from './game/defs/goal';
import { canPlayLeague } from 'src/replicant/match2/leagues/leagues.getters';
import { getMutex } from 'src/replicant/utils/mutex';
import { BoosterId } from './defs/booster';
import {
  getMatch2FrenzyTimeLeft,
  isMatch2FrenzyEnabled,
} from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { rubberbandingV2 } from './rubberbanding/rubberbanding.v2';
import {
  Match2Reward,
  isRandomReward,
  isTimedReward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { treasureExpressTicketRewards } from 'src/replicant/treasureExpress/treasureExpress.ruleset';
import {
  getDailyChallenge,
  isDailyChallengeAvailable,
  isDailyChallengeComplete,
} from 'src/replicant/match2/dailychallenge/match2-dailychallenge.getters';
import {
  getActiveRace,
  hasJoinedActiveRace,
} from 'src/replicant/match2/races/races.getters';
import {
  getStreakBattleRoyaleCurrentMilestone,
  isStreakBattleRoyaleActive,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import {
  getTreasureExpressTicketsByAttempts,
  isTreasureExpressAvailable,
} from 'src/replicant/treasureExpress/treasureExpress.getters';
import { getGameScene } from './game/getGameScene';

/**
 * Get the formatted timer text for the daily bonus. If the daily bonus is ready to be collected, the text will be "Collect".
 * @returns Formatted and localised timer/cta text.
 */
export function getDailyBonusTimerText() {
  const remaining = Match2.getters.getDailyBonusRemainingCooldown(
    StateObserver.getState().user,
    StateObserver.now(),
  );

  return remaining > 0
    ? formatDuration(remaining)
    : i18n('match2.dailyBonus.collect');
}

export function isDailyBonusReady() {
  const remaining = Match2.getters.getDailyBonusRemainingCooldown(
    StateObserver.getState().user,
    StateObserver.now(),
  );

  return remaining === 0;
}

/**
 * Combine multi-size goals into one - ie. small/large milk cartons (panther)
 */
export function consolidateGoals(goals: GoalsDef) {
  // Make a copy of the goals definition
  goals = { ...goals };

  // Loops through each set of predefined multi-size goals
  for (let index = 0; index < multiSizeGoals.length; index++) {
    const multiSizeGoalSet = multiSizeGoals[index].filter(
      (id) => goals[id] !== undefined,
    );

    // If there are less than 2 goals satisfied in the set, skip it
    if (multiSizeGoalSet.length < 2) continue;

    const total = multiSizeGoalSet.reduce((acc, id, index) => {
      const amount = goals[id] ?? 0;

      // Delete all the goals except the first one
      if (index > 0) delete goals[id];

      return acc + amount;
    }, 0);

    // Delete the original goal
    delete goals[multiSizeGoalSet[0]];

    // Apply the accumulated value to the first one
    goals[multiSizeGoals[index][0]] = total;
  }

  return goals;
}

/**
 * Get the formatted timer text for match2 frenzy event.
 * @returns Formatted timer text.
 */
export function getMatch2FrenzyTimerText() {
  const remaining = getMatch2FrenzyTimeLeft(
    StateObserver.getState().user,
    StateObserver.now(),
  );

  return formatMatch2Duration(remaining);
}

// Unique to Match2, without shortening to days
export function formatMatch2Duration(duration: number, compact = false) {
  duration = Math.floor(duration / 1000);
  const seconds = duration % 60;

  duration = Math.floor(duration / 60);
  const minutes = duration % 60;

  duration = Math.floor(duration / 60);
  const hours = compact ? duration : duration % 24;

  duration = Math.floor(duration / 24);
  const days = Math.floor(duration);

  let daysStr = '';
  let hoursStr = '';
  let minutesStr = '';
  let secondsStr = '';
  let prefix = '';

  if (compact) {
    hoursStr = `${prefix}${hours}:`;
    prefix = minutes >= 10 ? '' : '0';
    minutesStr = `${prefix}${minutes}:`;
    prefix = seconds >= 10 ? '' : '0';
    secondsStr = `${prefix}${seconds}`;
  } else {
    daysStr = days > 0 ? `${days}${i18n('duration.d')} ` : '';
    hoursStr = hours > 0 ? `${hours}${i18n('duration.h')} ` : '';
    minutesStr = minutes > 0 ? `${minutes}${i18n('duration.m')}` : '';
  }

  return `${daysStr}${hoursStr}${minutesStr}${secondsStr}`;
}

/**
 * Cheats hook for overriding the block generation type for a level
 */
let forceBlockgenOverride: BlockGenAlgorithm | undefined = undefined;
export function setCheatForceBlockGen(type: BlockGenAlgorithm | undefined) {
  forceBlockgenOverride = type;
}
export function getCheatForceBlockGen(): BlockGenAlgorithm | undefined {
  return forceBlockgenOverride;
}

export type InsideLevelRubberbandingOptions = {
  obstacleCompletion: number;
  moveCompletion: number;
  continues: number;
};

/** Block generators that are easier than the Masters League minimum. */
const belowRandomBlockGens = new Set<BlockGenAlgorithm>([
  'easy',
  'easy-v5-large-chains-2',
  'easy-v2-powerups',
]);

/**
 * Get the rubberbanded block generator for Masters League.
 *
 * @param step Player's current win ladder step.
 * @param insideLevelOpts Current in-level completion metrics.
 * @returns A block generator no easier than random.
 */
function getMastersLeagueBlockGen(
  step: number,
  insideLevelOpts?: InsideLevelRubberbandingOptions,
): BlockGenAlgorithm {
  // Calculate the player's current rubberbanded block generator.
  const blockGen = rubberbandingV2(step, insideLevelOpts);

  // Keep Masters League difficulty from dropping below random.
  return belowRandomBlockGens.has(blockGen) ? 'random' : blockGen;
}

/**
 * Get block generation type for a level
 * @param level level
 * @returns block generation type
 */
export function getBlockGen(
  mapDef: MapDef,
  level: number,
  insideLevelOpts?: InsideLevelRubberbandingOptions,
): BlockGenAlgorithm {
  // CHEAT OVERRIDES (TESTING ONLY)
  if (forceBlockgenOverride) {
    return forceBlockgenOverride;
  }

  // For leagues use custom blockgen.
  if (canPlayLeague(user())) {
    return getMastersLeagueBlockGen(
      user().match2.winLadderStep,
      insideLevelOpts,
    );
  }

  if (!mapDef.blockGen) {
    return 'random'; // default has always been 'random' in prod anyway (due to a past bug)
  }

  // Apply rubberbanding algorithm.
  if (mapDef.blockGen === 'rubberbanding') {
    const step = user().match2.winLadderStep;
    return rubberbandingV2(step, insideLevelOpts);
  }

  return mapDef.blockGen;
}

// Use mutex for storing in-game unlimited booster usage
const boosterMutex: Record<string, ReturnType<typeof getMutex>> = {
  dart: getMutex(),
  bullet: getMutex(),
  drill: getMutex(),
  roulette: getMutex(),
};

export function resetUnlimitedBoostersUsageInSession() {
  for (const key in boosterMutex) {
    const mutex = boosterMutex[key];
    mutex.release();
  }
}

export function setUnlimitedBoosterUsedInSession(booster: BoosterId) {
  const mutex = boosterMutex[booster];
  if (mutex.isLocked()) return;
  mutex.lock();
}

export function getUnlimitedBoosterUsedInSession(booster: BoosterId) {
  const mutex = boosterMutex[booster];
  return mutex.isLocked();
}

/**
 * Create a compensation object for rewards.
 * @returns
 */
export function createRewardCompensation() {
  return {
    bPoints: 0,
    gems: 0,
    energy: 0,
  };
}

/**
 * Reward compensation type
 */
export type RewardCompensation = ReturnType<typeof createRewardCompensation>;

/**
 * Update a reward compensation object with a reward.
 * @param compensation
 * @param reward
 * @param addOrSubtract
 * @returns
 */
export function updateRewardCompensation(
  compensation: RewardCompensation,
  reward: Match2Reward,
  addOrSubtract: 1 | -1,
) {
  if (isRandomReward(reward)) return;
  if (isTimedReward(reward)) return;

  switch (reward.type) {
    case 'bPoints':
      compensation.bPoints += reward.amount * addOrSubtract;
      break;
    case 'gems':
      compensation.gems += reward.amount * addOrSubtract;
      break;
    case 'energy':
      compensation.energy = reward.amount * addOrSubtract;
      break;
  }
}

export function getAchievementLosses() {
  const puzzleAttempts = Math.min(
    user().match2.losingStreak,
    treasureExpressTicketRewards.length - 1,
  );

  return {
    frenzy: getFrenzyCurrentAchievement(),
    dailyChallenge: getDailyChallengeCurrentAchievement(),
    race: getRaceCurrentAchievement(),
    fujiQuest: getFujiQuestCurrentAchievement(),
    treasureExpress: getTreasureExpressCurrentAchievement(puzzleAttempts),
    league: undefined,
  };
}

/**
 * Get frenzy achievement that will be lost upon quitting the current puzzle.
 */
function getFrenzyCurrentAchievement() {
  const isFrenzyActive = isMatch2FrenzyEnabled(user(), now());
  return isFrenzyActive
    ? getGameScene()?.getFrenzyObjectivesCount()
    : undefined;
}

/**
 * Get daily challenge achievement that will be lost upon quitting the current puzzle.
 */
function getDailyChallengeCurrentAchievement() {
  const isDailyChallengeActive =
    isDailyChallengeAvailable(user()) && !isDailyChallengeComplete(user());

  if (!isDailyChallengeActive) return undefined;

  const gameScene = getGameScene();

  if (!gameScene) return undefined;

  const dailyChallengeDay = user().match2.dailyChallenge?.day ?? 0;
  const dailyChallengeGoal = getDailyChallenge(dailyChallengeDay).goal;

  switch (dailyChallengeGoal.type) {
    // For block types, return the count of blocks broken
    case 'basicBlocks':
    case 'powerBlocks': {
      const { subType } = dailyChallengeGoal;
      if (subType === 'rocket') {
        const { rocketHorizontal = 0, rocketVertical = 0 } =
          gameScene.blocksBroken;
        return rocketHorizontal + rocketVertical;
      } else if (subType === 'cube') {
        const {
          cubeRed = 0,
          cubeBlue = 0,
          cubeGreen = 0,
          cubeYellow = 0,
          cubePurple = 0,
        } = gameScene.blocksBroken;
        return cubeRed + cubeBlue + cubeGreen + cubeYellow + cubePurple;
      } else {
        return gameScene.blocksBroken[subType];
      }
    }
    // For level completes, return 1 condition is satisfied
    case 'levelCompletes': {
      const { subType } = dailyChallengeGoal;
      if (subType === 'default' || user().match2.buruStreak.value >= 3) {
        return 1;
      }
      break;
    }
    case 'movesRemaining':
      // Skip this type as move pinching points to the user not having any moves left anyway
      break;
  }

  return undefined;
}

/**
 * Get race achievement that will be lost upon quitting the current puzzle.
 */
function getRaceCurrentAchievement() {
  const isRaceActive = hasJoinedActiveRace(user(), now());
  if (!isRaceActive) return undefined;
  const theme = getActiveRace(now())?.config.theme;
  // For crown theme, it's always 1 point per puzzle level.
  // Otherwise, it's the pending race score on that puzzle.
  return theme === 'crown' ? 1 : user().match2.pendingRaceScore?.score;
}

/**
 * Get fuji quest achievement that will be lost upon quitting the current puzzle.
 */
function getFujiQuestCurrentAchievement() {
  const isFujiQuestActive = isStreakBattleRoyaleActive(user(), now());
  return isFujiQuestActive
    ? getStreakBattleRoyaleCurrentMilestone(user(), now())
    : undefined;
}

/**
 * Get treasure express achievement that will be lost upon quitting the current puzzle.
 */
function getTreasureExpressCurrentAchievement(puzzleAttempts: number) {
  const isTreasureExpressActive = isTreasureExpressAvailable(user(), now());
  return isTreasureExpressActive
    ? getTreasureExpressTicketsByAttempts(puzzleAttempts)
    : undefined;
}
